import { type IsoLocale } from '@repo/shared/locale';
import { SRG_UI_LOCALE_BY_HOSTNAME } from 'util/srg-domains';
import type { DeploymentConfig, FirebaseConfig, SiloConfig, SiloDefaultFeatures } from 'util/stdtypes';

const cbcDefaultFeatures: SiloDefaultFeatures = {
    question: { aboutbanner: true },
};

const cbcDefaultSiloConfig: SiloConfig = {
    textVariants: {
        aboutBanner:
            'Learn about our [new conversation tools](https://cbchelp.cbc.ca/hc/en-ca/articles/31567413658525-What-are-these-new-conversational-tools-appearing-on-some-stories), powered by New_ Public',
    },
    titledWriterBadgeSource: require('./assets/logos/cbc/molecule.svg'),
    standalonePageConfig: {
        header: {
            logo: {
                desktop: require('./assets/logos/cbc/lockup.svg'),
                mobile: require('./assets/logos/cbc/lockup.svg'),
            },
            alt: 'CBC',
            logoLinkUrl: 'https://www.cbc.ca/',
        },
        footer: {
            linkUrl: 'https://www.cbc.ca/',
            linkText: 'cbc.ca',
            privacyNoticeLabel: 'New_ Public Privacy Notice',
            termsOfServiceLabel: 'New_ Public Terms of Service',
        },
    },
};

const zdfDefaultFeatures: SiloDefaultFeatures = {
    question: {
        satisfaction_survey: false,
        usability_survey: false,
    },
};

const rcDefaultSiloConfig: SiloConfig = {
    hideCookieNoticeBanner: true,
    defaultLanguage: 'fr-CA',
    titledWriterBadgeSource: require('./assets/logos/rc/molecule.svg'),
    titledWriterPredefinedOptions: ['Journaliste', 'Hôte de conversation'],
    standalonePageConfig: {
        header: {
            logo: {
                desktop: require('./assets/logos/rc/radio-canada.svg'),
                mobile: require('./assets/logos/rc/molecule.svg'),
            },
            alt: 'Radio-Canada',
            logoLinkUrl: 'https://ici.radio-canada.ca/',
        },
        footer: {
            linkUrl: 'https://ici.radio-canada.ca/',
            linkText: 'Radio-Canada',
        },
    },
};

const testDefaultSiloConfig: SiloConfig = {
    standalonePageConfig: {
        header: {
            logo: {
                desktop: require('./assets/logos/test/desktop.svg'),
                mobile: require('./assets/logos/test/mobile.svg'),
            },
            alt: 'Public Spaces Incubator',
            logoLinkUrl: 'https://newpublic.org/psi',
        },
        footer: {
            linkUrl: 'https://newpublic.org/psi',
            linkText: 'Public Spaces Incubator',
        },
    },
};

const rtbfDefaultSiloConfig: SiloConfig = {
    defaultLanguage: 'fr-BE',
};

const eoDefaultSiloConfig: SiloConfig = {
    defaultLanguage: 'nl-NL',
    standalonePageConfig: {
        header: {
            logo: {
                desktop: require('./assets/logos/eo/lockup.svg'),
                mobile: require('./assets/logos/eo/lockup.svg'),
            },
            alt: 'EO',
            logoLinkUrl: 'https://www.eo.nl/',
        },
        footer: {
            linkUrl: 'https://www.eo.nl/',
            linkText: 'eo.nl',
        },
    },
};

// Per-silo overrides. A silo needs an entry here to be known at all — the keys
// feed `knownSilos`, and so `allowedSilos` — so an entry with no overrides is
// an empty object rather than a missing key.
const defaultSiloConfig: Record<string, SiloConfig> = {
    global: {},
    eo: eoDefaultSiloConfig,
    cbc: cbcDefaultSiloConfig,
    cbcInt: cbcDefaultSiloConfig,
    rc: rcDefaultSiloConfig,
    rcInt: rcDefaultSiloConfig,
    rtbf: rtbfDefaultSiloConfig,
    np: testDefaultSiloConfig,
    demo: testDefaultSiloConfig,
    test: testDefaultSiloConfig,
    e2etest: {
        ...testDefaultSiloConfig,
        titledWriterPredefinedOptions: ['Journalist', 'Conversation host'],
        titledWriterBadgeSource: require('./assets/logos/test/mobile.svg'),
    },
    styledemo: {
        themeKey: 'zdfHeuteDark',
    },
    srg: {
        multiLanguage: true,
        languageFlagCountryPrefix: 'CH',
        standalonePageConfig: {
            header: {
                // fr and en share the "dialogue" wordmark; de and rm share
                // "dialog"
                logoByLanguage: {
                    fr: { logo: require('./assets/logos/srg/dialogue-endorsement.svg'), alt: 'SRG SSR Dialogue' },
                    en: { logo: require('./assets/logos/srg/dialogue-endorsement.svg'), alt: 'SRG SSR Dialogue' },
                    de: { logo: require('./assets/logos/srg/dialog-endorsement.svg'), alt: 'SRG SSR Dialog' },
                    it: { logo: require('./assets/logos/srg/dialogo-endorsement.svg'), alt: 'SRG SSR Dialogo' },
                    rm: { logo: require('./assets/logos/srg/dialog-endorsement.svg'), alt: 'SRG SSR Dialog' },
                },
                // Production dialog domains, derived from the per-BU login
                // mapping in util/srg-domains.ts
                logoLanguageByHostname: SRG_UI_LOCALE_BY_HOSTNAME,
            },
        },
    },
    'multi-test': { multiLanguage: true },
    kika: {
        ...testDefaultSiloConfig,
        defaultLanguage: 'de-DE',
    },
    zdf: {
        ...testDefaultSiloConfig,
        defaultLanguage: 'de-DE',
        loginBranding: {
            loginHeadingLabel: 'Zur Diskussion',
            loginSubheadingLabel:
                'Um teilzunehmen, registriere dich für den Test-Betrieb unseres Konversationsangebots „ZDFspaces“ oder melde dich an.',
            showCookieNotice: false,
            brandLogoImages: ['images/ZDF_logo.svg'],
        },
        standalonePageConfig: {
            header: {
                logo: {
                    desktop: require('./assets/logos/zdf/logo.svg'),
                    mobile: require('./assets/logos/zdf/logo.svg'),
                },
                alt: 'ZDF',
                logoLinkUrl: 'https://www.zdf.de/',
            },
        },
    },
    wiki: testDefaultSiloConfig,
};

const knownSilos = Object.keys(defaultSiloConfig);

export const siloLanguagesConfig: Record<string, IsoLocale[]> = {
    eo: ['nl-NL'],
    srg: ['fr-CH', 'de-CH', 'it-CH', 'rm-CH', 'en-US'],
    zdf: ['de-DE'],
    kika: ['de-DE'],
    cbc: ['en-US', 'fr-CA'],
    rc: ['fr-FR', 'fr-CA', 'en-US'],
    rtbf: ['fr-FR', 'fr-BE'],
    default: ['en-US', 'fr-FR', 'fr-CH', 'fr-CA', 'fr-BE', 'de-DE', 'de-CH', 'it-IT', 'it-CH', 'rm-CH', 'nl-NL'],
};

const localFirebaseConfig: FirebaseConfig = {
    apiKey: 'fake-api-key',
    authDomain: 'demo-psi-local.firebaseapp.com',
    projectId: 'demo-psi-local',
    messagingSenderId: '123456789012',
    appId: 'fake-app-id',
};

const productionFirebaseConfig: FirebaseConfig = {
    apiKey: 'AIzaSyDIg3OR3i51VYrUyUd_L5iIownjdSnExlc',
    authDomain: 'np-psi-dev.firebaseapp.com',
    projectId: 'np-psi-dev',
    messagingSenderId: '768032889623',
    appId: '1:768032889623:web:634a1604eda03820ab7552',
};

const stagingFirebaseConfig: FirebaseConfig = {
    apiKey: 'AIzaSyAG6jDk_GQRPtCby-amS6LoMWPnvKCDw8Q',
    authDomain: 'np-psi-staging.firebaseapp.com',
    projectId: 'np-psi-staging',
    messagingSenderId: '1040161687473',
    appId: '1:1040161687473:web:9f1b29060c33946325df90',
};

const researchFirebaseConfig: FirebaseConfig = {
    apiKey: 'AIzaSyBycpLc5-iI0020b8TEgQBzLPMJ3Iz3FDg',
    authDomain: 'np-psi-research.firebaseapp.com',
    projectId: 'np-psi-research',
    messagingSenderId: '669033105407',
    appId: '1:669033105407:web:2a68f734a770a8577889ca',
};

const rcDevFirebaseConfig: FirebaseConfig = {
    apiKey: 'AIzaSyBBmARny3o_UO4mS-fbec9ncTpOV1kTshY',
    authDomain: 'apppsi-40af9.firebaseapp.com',
    projectId: 'apppsi-40af9',
    messagingSenderId: '563226474199',
    appId: '1:563226474199:web:a8ff571ea7f016b11aa292',
};

const rcProdFirebaseConfig: FirebaseConfig = {
    apiKey: 'AIzaSyAupzFkfGhu4Do7t4B78emtcEIkI8gI0Cc',
    authDomain: 'rc-prod-psi-product.firebaseapp.com',
    projectId: 'rc-prod-psi-product',
    messagingSenderId: '935278692468',
    appId: '1:935278692468:web:4ac6ef065ab675e2daa921',
};

const isLocalMongoDevelopment = process.env.EXPO_PUBLIC_DATABASE_ADAPTER === 'mongodb';

const localDeploymentConfig: DeploymentConfig = {
    apiBaseUrl: process.env.EXPO_PUBLIC_API_BASE_URL || `http://${window.location.hostname}:5010/api/`,
    baseUrl: `${window.location.origin}/`,
    firebaseConfig: localFirebaseConfig,
    emulateFirebase: !isLocalMongoDevelopment,
    defaultToAdminDash: true,
    ssoDebug: true,
    enableDevLogin: true,
    siloConfig: defaultSiloConfig,
    allowedSilos: knownSilos,
    errorMonitoringDsn: undefined, // Add your error monitoring DSN here
    defaultLanguage: 'en-US',
    defaultFeatures: {
        test: {},
        testDefault: {
            question: {
                commentslider: true,
            },
        },
        cbc: cbcDefaultFeatures,
        cbcInt: cbcDefaultFeatures,
        zdf: zdfDefaultFeatures,
        srg: {
            question: {
                'srg-disable-replies-on-audience-input': true,
                'show-original-language': true,
            },
            profile: {
                profiledisablephotoedit: true,
                relaxedcustomnames: true,
            },
        },
    },
};

const eoAzureTestDeploymentConfig: DeploymentConfig = {
    apiBaseUrl: 'https://capp-test-psi.orangebay-03f093d9.northeurope.azurecontainerapps.io/api/',
    baseUrl: 'https://psi.test.eo.nl/',
    enableDevLogin: true,
    defaultToAdminDash: true,
    siloConfig: defaultSiloConfig,
    allowedSilos: ['eo'],
};

const eoAzureProdDeploymentConfig: DeploymentConfig = {
    apiBaseUrl: 'https://capp-prod-psi.graywater-463d3e28.northeurope.azurecontainerapps.io/api/',
    baseUrl: 'https://psi.eo.nl/',
    enableDevLogin: false,
    defaultToAdminDash: true,
    siloConfig: defaultSiloConfig,
    allowedSilos: ['eo'],
};

const productionDeploymentConfig: DeploymentConfig = {
    baseUrl: 'https://psi.newpublic.org/',
    firebaseConfig: productionFirebaseConfig,
    siloConfig: defaultSiloConfig,
    allowedSilos: knownSilos,
    errorMonitoringDsn: 'https://bb4e368b8fcf4e4996d273da8193652b@psi-bugsink-eu.newpublic.org/3',
    defaultFeatures: {
        cbc: cbcDefaultFeatures,
        zdf: zdfDefaultFeatures,
        // multi-test is the test-only multilingual silo (used by Jest tests
        // and Storybook); mirror SRG's defaults so multilingual UI exercises.
        'multi-test': { question: { 'show-original-language': true } },
    },
};

const stagingDeploymentConfig: DeploymentConfig = {
    baseUrl: 'https://np-psi-staging.firebaseapp.com/',
    firebaseConfig: stagingFirebaseConfig,
    siloConfig: defaultSiloConfig,
    allowedSilos: knownSilos,
    errorMonitoringDsn: 'https://68747617a4b7400f8c21e99f99e00734@psi-bugsink-eu.newpublic.org/1',
    defaultFeatures: { cbcInt: cbcDefaultFeatures, zdf: zdfDefaultFeatures },
};

export const deploymentConfigMap: Record<string, DeploymentConfig> = {
    local: localDeploymentConfig,
    'eo-azure-test': eoAzureTestDeploymentConfig,
    'eo-azure-prod': eoAzureProdDeploymentConfig,
    production: productionDeploymentConfig,
    staging: stagingDeploymentConfig,
    research: {
        baseUrl: 'https://np-psi-research.firebaseapp.com/',
        firebaseConfig: researchFirebaseConfig,
        siloConfig: defaultSiloConfig,
        errorMonitoringDsn: 'https://421e790ace4c46b1968b56e35ed89f17@psi-bugsink-eu.newpublic.org/4',
    },
    preview: {
        defaultToAdminDash: true,
        firebaseConfig: stagingFirebaseConfig,
        siloConfig: defaultSiloConfig,
        defaultFeatures: { cbcInt: cbcDefaultFeatures, zdf: zdfDefaultFeatures },
    },
    rcDev: {
        baseUrl: 'https://dev-conversations.radio-canada.ca/',
        firebaseConfig: rcDevFirebaseConfig,
        siloConfig: defaultSiloConfig,
        allowedSilos: ['rc', 'test'],
    },
    rcProd: {
        baseUrl: 'https://conversations.radio-canada.ca/',
        firebaseConfig: rcProdFirebaseConfig,
        siloConfig: defaultSiloConfig,
        allowedSilos: ['rc'],
        errorMonitoringDsn: 'https://5edb20cd3b0244168174b790317a7b1f@psi-bugsink-eu.newpublic.org/5',
    },
    cbcLocal: {
        ...localDeploymentConfig,
        baseUrl: `${window.location.origin}/conversations/`,
        basePath: 'conversations/',
        apiBaseUrl: 'http://localhost:5010/api/',
    },
    cbcClientProd: {
        ...productionDeploymentConfig,
        baseUrl: `${window.location.origin}/conversations/`,
        basePath: 'conversations/',
        apiBaseUrl: 'https://psi.newpublic.org/api/',
    },
    cbcClientStaging: {
        ...stagingDeploymentConfig,
        baseUrl: `${window.location.origin}/conversations/`,
        basePath: 'conversations/',
        apiBaseUrl: 'https://np-psi-staging.firebaseapp.com/api/',
    },
};

export const domainToDeploymentMap: Record<string, string> = {
    'np-psi-dev.firebaseapp.com': 'production', // Note: Despite confusing name, np-psi-dev is the production Firebase project
    'np-psi-dev.web.app': 'production', // Additional Firebase hosting domain for production
    'np-psi-staging.firebaseapp.com': 'staging',
    'np-psi-staging.web.app': 'staging',
    'np-psi-research.firebaseapp.com': 'research',
    'np-psi-research.web.app': 'research',
    'psi.newpublic.org': 'production',
    'conversations.radio-canada.ca': 'rcProd',
    'rc-prod-psi-product.web.app': 'rcProd', // Firebase hosting domain for RC production
    'dev-conversations.radio-canada.ca': 'rcDev',
    'local.cbc.ca': 'cbcLocal',
    'www.cbc.ca': 'cbcClientProd',
    'www.dev.nm.cbc.ca': 'cbcClientStaging',
    'www.qa.nm.cbc.ca': 'cbcClientStaging',
    'apppsi-40af9.web.app': 'rcDev', // Firebase hosting for RC dev (project: apppsi-40af9)
    'apppsi-40af9.firebaseapp.com': 'rcDev', // Additional hosting for RC dev
    '127.0.0.1': 'local', // Local IP address access
    localhost: 'local',
    test: 'production',
    'witty-ground-0442dbb03.7.azurestaticapps.net': 'eo-azure-test',
    'psi.test.eo.nl': 'eo-azure-test',
    'psi.eo.nl': 'eo-azure-prod',
};

/**
 * Per-domain default UI language override.
 *
 * Re-exported from `util/domain-language-map` so the runtime app can keep
 * importing it from `psi.config`. The data itself lives in the standalone
 * module because the inject-html-lang Node-side build script
 * (`scripts/inject-html-lang.ts`) needs to consume it without triggering the
 * top-level `window.location.*` / `require('./assets/*.svg')` references that
 * `psi.config.ts` carries. Partner overrides should go in
 * `util/domain-language-map.ts`.
 *
 * **Node-side consumers should import from `util/domain-language-map`
 * directly** — re-importing through this barrel would re-execute
 * `psi.config.ts`'s top-level browser-only code and crash. Runtime
 * (browser) consumers are unaffected and can keep importing from either
 * location.
 */
export { domainToLanguageMap } from 'util/domain-language-map';
